import { useEffect, useRef, useState } from 'react'
import { useModal } from '../../context/ModalContext'
import { useGuides } from '../../hooks/useGuides'
import { useTopics } from '../../hooks/useTopics'
import { notebookFileUrl, uploadNotebookFile } from '../../services/notebooks'
import { standardName } from '../../utils/standardRef'
import Markdown from '../chat/Markdown'
import SketchBlock from './SketchBlock'
import {
  BLOCK_TYPES,
  emptyHeading,
  emptyParagraph,
  emptySketch,
  emptyText,
  filterBlockTypes,
  guideBlock,
  imageBlock,
  insertAfter,
  itemBlock,
  moveBlock,
  pageBlock,
  pdfBlock,
  slashQuery,
  topicBlock,
} from './blockUtils'

const Grip = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
    <circle cx="9" cy="7" r="1.2" fill="currentColor" />
    <circle cx="15" cy="7" r="1.2" fill="currentColor" />
    <circle cx="9" cy="12" r="1.2" fill="currentColor" />
    <circle cx="15" cy="12" r="1.2" fill="currentColor" />
    <circle cx="9" cy="17" r="1.2" fill="currentColor" />
    <circle cx="15" cy="17" r="1.2" fill="currentColor" />
  </svg>
)

function TypeMenu({ types, active, onPick }) {
  if (!types.length) return <div className="nb-menu-empty">No matching blocks</div>
  return types.map((entry, index) => (
    <button
      key={entry.type}
      type="button"
      className={'nb-menu-item' + (index === active ? ' is-active' : '')}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onPick(entry.type)}
    >
      <span className="nb-menu-label">{entry.label}</span>
      <span className="nb-menu-hint">{entry.hint}</span>
    </button>
  ))
}

function PickMenu({ options, empty, getLabel, onPick }) {
  if (!options.length) return <div className="nb-menu-empty">{empty}</div>
  return options.map((option) => (
    <button
      key={option._id}
      type="button"
      className="nb-menu-item"
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onPick(option)}
    >
      <span className="nb-menu-label">{getLabel(option)}</span>
    </button>
  ))
}

const SOURCE_NAMES = { searchroom: 'Searchroom', global: 'Global AI Search' }

function AnswerCard({ answer, selectedItemId, onSelectItem }) {
  const source = [SOURCE_NAMES[answer.source], answer.sourceLabel]
    .filter((part, index, parts) => part && parts.indexOf(part) === index)
    .join(' · ')
  return (
    <div className="nb-answer">
      <div className="nb-answer-head">
        <span className="nb-embed-kind">AI answer</span>
        <span className="nb-answer-source">
          {source}
          {answer.model ? ` · ${answer.model}` : ''}
        </span>
      </div>
      {answer.question && <div className="nb-answer-question">{answer.question}</div>}
      <Markdown>{answer.text}</Markdown>
      {answer.items.length > 0 && (
        <div className="nb-answer-refs">
          {answer.items.map((item) => (
            <button
              key={item._id}
              type="button"
              className={
                'nb-answer-ref' + (String(item._id) === String(selectedItemId) ? ' is-selected' : '')
              }
              onClick={() => onSelectItem?.(item)}
            >
              <span>{item.label}</span>
              <span className="nb-answer-ref-meta">{standardName(item.standard)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Notion-style page: a title, a block-type bar, then an ordered list of blocks.
// "/" inside a writing block opens the same types. Drag the handle to reorder.
function imageFilesFromClipboard(data) {
  const files = []
  const seen = new Set()
  const push = (file) => {
    if (!file || !file.type?.startsWith('image/') || seen.has(file)) return
    seen.add(file)
    files.push(file)
  }
  if (data?.files) Array.from(data.files).forEach(push)
  if (data?.items) {
    Array.from(data.items).forEach((item) => {
      if (item.kind === 'file' && item.type.startsWith('image/')) push(item.getAsFile())
    })
  }
  return files
}

function NotebookEditor({
  title,
  blocks,
  onTitleChange,
  onBlocksChange,
  selectedItemId,
  onSelectItem,
  onSelectPdf,
  onCreatePage,
  onOpenPage,
  onDeletePage,
  pageTitle,
  wide = false,
}) {
  const { openModal } = useModal()
  const { guides, upsertGuide } = useGuides()
  const { topics } = useTopics()
  const [focusId, setFocusId] = useState(null)
  const [slashId, setSlashId] = useState(null)
  const [slashActive, setSlashActive] = useState(0)
  const [picker, setPicker] = useState(null)
  const [draggingId, setDraggingId] = useState(null)
  const inputRefs = useRef({})
  const anchorRef = useRef(null)
  const imageInputRef = useRef(null)
  const pdfInputRef = useRef(null)
  const pendingFileReplace = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [busyLabel, setBusyLabel] = useState('')
  const [uploadError, setUploadError] = useState('')

  useEffect(() => {
    if (!focusId) return
    const node = inputRefs.current[focusId]
    if (node && document.activeElement !== node) node.focus()
  }, [focusId, blocks])

  const commit = (next, focus) => {
    onBlocksChange(next)
    if (focus) setFocusId(focus)
  }

  const insertWriting = (factory, afterId) => {
    const block = factory()
    const anchor = afterId || anchorRef.current
    commit(anchor ? insertAfter(blocks, anchor, block) : [...blocks, block], block.id)
    setSlashId(null)
    setPicker(null)
  }

  const placeBlocks = (created, afterId) => {
    const list = created.filter(Boolean)
    if (!list.length) return
    let next = blocks
    const anchor = afterId || anchorRef.current
    if (anchor && next.some((block) => block.id === anchor)) {
      const index = next.findIndex((block) => block.id === anchor)
      next = next.slice()
      next.splice(index + 1, 0, ...list)
    } else {
      next = [...next, ...list]
    }
    commit(next, list[0].id)
    setPicker(null)
    setSlashId(null)
  }

  const replaceBlock = (id, created) => {
    const list = created.filter(Boolean)
    if (!list.length) return
    const index = blocks.findIndex((block) => block.id === id)
    const next = blocks.slice()
    if (index < 0) next.push(...list)
    else next.splice(index, 1, ...list)
    commit(next, list[0].type === 'text' || list[0].type === 'paragraph' || list[0].type === 'heading' ? list[0].id : null)
    setSlashId(null)
    setPicker(null)
  }

  const pickItems = (afterId, replaceId) => {
    openModal('notebook-items', {
      selected: [],
      onSave: (picked) => {
        const created = (picked || []).map(itemBlock).filter(Boolean)
        if (replaceId) replaceBlock(replaceId, created)
        else placeBlocks(created, afterId)
      },
    })
  }

  const addByType = (type, afterId, replaceId) => {
    if (type === 'text') {
      const block = emptyText()
      if (replaceId) replaceBlock(replaceId, [block])
      else insertWriting(() => block, afterId)
      return
    }
    if (type === 'paragraph') {
      const block = emptyParagraph()
      if (replaceId) replaceBlock(replaceId, [block])
      else insertWriting(() => block, afterId)
      return
    }
    if (type === 'heading') {
      const block = emptyHeading()
      if (replaceId) replaceBlock(replaceId, [block])
      else insertWriting(() => block, afterId)
      return
    }
    if (type === 'item') {
      pickItems(afterId, replaceId)
      return
    }
    if (type === 'page') {
      if (!onCreatePage) return
      setSlashId(null)
      setBusyLabel('Creating page…')
      setUploadError('')
      onCreatePage()
        .then((created) => {
          const block = pageBlock(created)
          if (!block) return
          if (replaceId) replaceBlock(replaceId, [block])
          else placeBlocks([block], afterId)
        })
        .catch((err) => setUploadError(err.message || 'Could not create the page.'))
        .finally(() => setBusyLabel(''))
      return
    }
    if (type === 'sketch') {
      const block = emptySketch()
      if (replaceId) replaceBlock(replaceId, [block])
      else placeBlocks([block], afterId)
      return
    }
    if (type === 'image' || type === 'pdf') {
      pendingFileReplace.current = replaceId || null
      setSlashId(null)
      ;(type === 'image' ? imageInputRef : pdfInputRef).current?.click()
      return
    }
    if (!replaceId && picker?.kind === type && !picker.replaceId) {
      setPicker(null)
      return
    }
    setPicker({ kind: type, afterId: replaceId ? null : afterId || anchorRef.current, replaceId })
    setSlashId(null)
  }

  const updateBlock = (id, patch) => {
    onBlocksChange(blocks.map((block) => (block.id === id ? { ...block, ...patch } : block)))
  }

  const dropBlock = (id) => {
    const index = blocks.findIndex((block) => block.id === id)
    const next = blocks.filter((block) => block.id !== id)
    const neighbor = next[index - 1] || next[index]
    commit(next, neighbor?.id || null)
    if (slashId === id) setSlashId(null)
  }

  const removeBlock = (id) => {
    const block = blocks.find((entry) => entry.id === id)
    if (block?.type === 'page' && block.pageId && onDeletePage) {
      onDeletePage({
        pageId: block.pageId,
        title: pageTitle?.(block.pageId) || block.title,
        onDeleted: () => dropBlock(id),
      })
      return
    }
    dropBlock(id)
  }

  const onWritingKeyDown = (event, block) => {
    const query = slashQuery(block.text)
    const types = query == null ? [] : filterBlockTypes(query)
    if (query != null && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault()
      setSlashActive((current) => {
        if (!types.length) return 0
        const delta = event.key === 'ArrowDown' ? 1 : -1
        return (current + delta + types.length) % types.length
      })
      return
    }
    if (query != null && event.key === 'Enter' && types.length) {
      event.preventDefault()
      addByType(types[Math.min(slashActive, types.length - 1)].type, null, block.id)
      return
    }
    if (query != null && event.key === 'Escape') {
      event.preventDefault()
      setSlashId(null)
      return
    }
    if (event.key === 'Enter' && block.type !== 'paragraph' && query == null) {
      event.preventDefault()
      const created = emptyText()
      commit(insertAfter(blocks, block.id, created), created.id)
      return
    }
    if (event.key === 'Backspace' && !block.text) {
      event.preventDefault()
      removeBlock(block.id)
    }
  }

  const onDragStart = (event, id) => {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', id)
    setDraggingId(id)
  }

  const allowDrop = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const onDrop = (event, targetId) => {
    event.preventDefault()
    const fromId = event.dataTransfer.getData('text/plain') || draggingId
    setDraggingId(null)
    if (fromId) onBlocksChange(moveBlock(blocks, fromId, targetId))
  }

  const uploadAndInsert = async (fileList, kind) => {
    const files = Array.from(fileList || [])
    if (!files.length || uploading) return
    setUploading(true)
    setUploadError('')
    try {
      const created = []
      for (const file of files) {
        const descriptor = await uploadNotebookFile(file)
        created.push(kind === 'pdf' ? pdfBlock(descriptor) : imageBlock(descriptor))
      }
      const replaceId = pendingFileReplace.current
      pendingFileReplace.current = null
      if (replaceId) replaceBlock(replaceId, created)
      else placeBlocks(created, anchorRef.current)
    } catch (err) {
      setUploadError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const onPaste = (event) => {
    const files = imageFilesFromClipboard(event.clipboardData)
    if (!files.length) return
    event.preventDefault()
    uploadAndInsert(files, 'image')
  }

  const slashBlock = blocks.find((block) => block.id === slashId)
  const slashTypes = slashBlock ? filterBlockTypes(slashQuery(slashBlock.text) || '') : []

  return (
    <div className={'nb-editor' + (wide ? ' is-wide' : '')} onPaste={onPaste}>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(event) => {
          uploadAndInsert(event.target.files, 'image')
          event.target.value = ''
        }}
      />
      <input
        ref={pdfInputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        hidden
        onChange={(event) => {
          uploadAndInsert(event.target.files, 'pdf')
          event.target.value = ''
        }}
      />
      <input
        className="nb-editor-title"
        type="text"
        value={title}
        placeholder="Untitled"
        aria-label="Page title"
        onChange={(event) => onTitleChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            insertWriting(emptyText)
          }
        }}
      />

      <div className="nb-typebar" role="toolbar" aria-label="Add a block">
        {BLOCK_TYPES.map((entry) => (
          <button
            key={entry.type}
            type="button"
            className={'nb-typebar-btn' + (picker?.kind === entry.type ? ' is-on' : '')}
            onClick={() => addByType(entry.type)}
          >
            {entry.label}
          </button>
        ))}
      </div>
      {(uploading || busyLabel) && (
        <div className="nb-upload-status">{busyLabel || 'Uploading…'}</div>
      )}
      {uploadError && <div className="nb-upload-status is-error">{uploadError}</div>}

      {picker && (
        <div className="nb-menu" role="listbox">
          {picker.kind === 'guide' ? (
            <>
              <PickMenu
                options={guides}
                empty="No guides yet."
                getLabel={(guide) => guide.title || guide.name || 'Guide'}
                onPick={(guide) => {
                  const block = guideBlock(guide)
                  if (picker.replaceId) replaceBlock(picker.replaceId, [block])
                  else placeBlocks([block], picker.afterId)
                }}
              />
              <button
                type="button"
                className="nb-menu-item"
                onClick={() =>
                  openModal('guide-form', {
                    onSaved: (guide) => {
                      if (!guide) return
                      upsertGuide(guide)
                      const block = guideBlock(guide)
                      if (picker.replaceId) replaceBlock(picker.replaceId, [block])
                      else placeBlocks([block], picker.afterId)
                    },
                  })
                }
              >
                <span className="nb-menu-label">+ New guide</span>
              </button>
            </>
          ) : (
            <PickMenu
              options={topics}
              empty="No topics yet."
              getLabel={(topic) => topic.name || 'Topic'}
              onPick={(topic) => {
                const block = topicBlock(topic)
                if (picker.replaceId) replaceBlock(picker.replaceId, [block])
                else placeBlocks([block], picker.afterId)
              }}
            />
          )}
        </div>
      )}

      <div
        className={'nb-editor-blocks' + (draggingId ? ' is-dragging' : '')}
        onDragEnd={() => setDraggingId(null)}
      >
        {blocks.map((block) => (
          <div
            className={
              'nb-block' +
              (block.type === 'item' && String(block.item?._id) === String(selectedItemId) ? ' is-selected' : '') +
              (block.type === 'pdf' && selectedItemId === `pdf:${block.fileId}` ? ' is-selected' : '') +
              (block.id === draggingId ? ' is-dragging' : '')
            }
            key={block.id}
            onDragOver={allowDrop}
            onDrop={(event) => onDrop(event, block.id)}
            onFocus={() => {
              anchorRef.current = block.id
            }}
          >
            <div
              className="nb-block-grip"
              title="Drag to reorder"
              aria-label="Drag to reorder"
              role="button"
              tabIndex={0}
              draggable
              onDragStart={(event) => onDragStart(event, block.id)}
              onDragEnd={() => setDraggingId(null)}
            >
              <Grip />
            </div>

            {block.type === 'heading' || block.type === 'text' || block.type === 'paragraph' ? (
              <div className="nb-block-field">
                {block.type === 'paragraph' ? (
                  <textarea
                    ref={(node) => {
                      inputRefs.current[block.id] = node
                    }}
                    className="nb-block-text is-paragraph"
                    value={block.text}
                    placeholder="Paragraph, or type '/' for blocks"
                    aria-label="Paragraph"
                    rows={Math.max(2, (block.text.match(/\n/g) || []).length + 1)}
                    onChange={(event) => {
                      const text = event.target.value
                      updateBlock(block.id, { text })
                      setSlashId(slashQuery(text) != null ? block.id : null)
                      setSlashActive(0)
                    }}
                    onKeyDown={(event) => onWritingKeyDown(event, block)}
                    onFocus={() => {
                      anchorRef.current = block.id
                      setFocusId(block.id)
                      setSlashId(slashQuery(block.text) != null ? block.id : null)
                    }}
                  />
                ) : (
                  <input
                    ref={(node) => {
                      inputRefs.current[block.id] = node
                    }}
                    className={block.type === 'heading' ? 'nb-block-heading' : 'nb-block-text'}
                    type="text"
                    value={block.text}
                    placeholder={block.type === 'heading' ? 'Heading' : "Type '/' for blocks"}
                    aria-label={block.type === 'heading' ? 'Heading' : 'Text'}
                    onChange={(event) => {
                      const text = event.target.value
                      updateBlock(block.id, { text })
                      setSlashId(slashQuery(text) != null ? block.id : null)
                      setSlashActive(0)
                    }}
                    onKeyDown={(event) => onWritingKeyDown(event, block)}
                    onFocus={() => {
                      anchorRef.current = block.id
                      setFocusId(block.id)
                      setSlashId(slashQuery(block.text) != null ? block.id : null)
                    }}
                  />
                )}
                {slashId === block.id && (
                  <div className="nb-menu nb-menu-float" role="listbox">
                    <TypeMenu
                      types={slashTypes}
                      active={slashActive}
                      onPick={(type) => addByType(type, null, block.id)}
                    />
                  </div>
                )}
              </div>
            ) : block.type === 'item' ? (
              <button
                type="button"
                className="nb-embed"
                onClick={() => onSelectItem?.(block.item)}
              >
                <span className="nb-embed-kind">Item</span>
                <span className="nb-embed-label">{block.item?.label || 'Item'}</span>
                <span className="nb-embed-meta">
                  {standardName(block.item?.standard)}
                  {block.item?.pageStart != null ? ` · p.${block.item.pageStart}` : ''}
                </span>
              </button>
            ) : block.type === 'image' ? (
              <img
                className="nb-image"
                src={notebookFileUrl(block.fileId)}
                alt={block.name || 'Image'}
              />
            ) : block.type === 'page' ? (
              <button
                type="button"
                className="nb-embed"
                onClick={() =>
                  onOpenPage?.({ _id: block.pageId, title: pageTitle?.(block.pageId) || block.title })
                }
              >
                <span className="nb-embed-kind">Page</span>
                <span className="nb-embed-label">
                  {pageTitle?.(block.pageId) || block.title || 'Untitled'}
                </span>
              </button>
            ) : block.type === 'pdf' ? (
              <button
                type="button"
                className="nb-embed"
                onClick={() => onSelectPdf?.({ fileId: block.fileId, name: block.name })}
              >
                <span className="nb-embed-kind">PDF</span>
                <span className="nb-embed-label">{block.name || 'PDF'}</span>
              </button>
            ) : block.type === 'guide' ? (
              <button
                type="button"
                className="nb-embed"
                onClick={() => openModal('guide-view', { guide: block.guide })}
              >
                <span className="nb-embed-kind">Guide</span>
                <span className="nb-embed-label">{block.guide?.title || 'Guide'}</span>
              </button>
            ) : block.type === 'sketch' ? (
              <SketchBlock block={block} onChange={(patch) => updateBlock(block.id, patch)} />
            ) : block.type === 'answer' ? (
              <AnswerCard
                answer={block.answer}
                selectedItemId={selectedItemId}
                onSelectItem={onSelectItem}
              />
            ) : (
              <div className="nb-embed is-static">
                <span className="nb-embed-kind">Topic</span>
                <span className="nb-embed-label">{block.topic?.name || 'Topic'}</span>
              </div>
            )}

            <button
              type="button"
              className="nb-block-remove"
              title="Remove block"
              aria-label="Remove block"
              onClick={() => removeBlock(block.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {blocks.length === 0 && (
        <button type="button" className="nb-editor-empty" onClick={() => insertWriting(emptyText)}>
          Click to start writing, or pick a block above.
        </button>
      )}
    </div>
  )
}

export default NotebookEditor
